import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  GridLegacy as Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  ShowChart as ShowChartIcon,
  EventNote as EventNoteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../services/api';

// Types
interface SellerData {
  userId: number;
  username: string;
  salesTrackerId?: string;
  totalSales: number;
  totalCalls: number;
  loginHours: string | number;
  averageUtilization: string | number;
  averageTalkPercent: string | number;
  hoursCommitted: string | number;
}

interface CoachingResponse {
  success: boolean;
  coachingData: SellerData[];
  weekStart: string;
  weekEnd: string;
}

interface TeamAverages {
  totalSales: number;
  avgSales: string;
  totalCalls: number;
  avgCalls: string;
  avgLoginHours: string;
  avgUtilization: string;
  avgTalkPercent: string;
  avgHoursCommitted: string;
}

interface SortConfig {
  key: keyof SellerData;
  direction: 'asc' | 'desc';
}

type DateFilter = 'currentWeek' | 'previousWeek';
type UtilizationColor = 'success' | 'warning' | 'error';

const PreviousWeekCoaching: React.FC = () => {
  const [coachingData, setCoachingData] = useState<SellerData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [weekRange, setWeekRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'totalSales', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<DateFilter>('previousWeek');

  useEffect(() => {
    fetchCoachingData();
  }, [dateFilter]);

  const fetchCoachingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getPreviousWeekCoaching();

      if (response.success) {
        setCoachingData(response.coachingData);
        setWeekRange({
          start: formatDate(response.weekStart),
          end: formatDate(response.weekEnd)
        });
      }
    } catch (err: any) {
      console.error('Error fetching coaching data:', err);
      setError(err.response?.data?.message || 'Failed to load coaching data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSort = (key: keyof SellerData) => {
    let direction: 'asc' | 'desc' = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    let sortableData = [...coachingData];
    
    if (searchTerm) {
      sortableData = sortableData.filter(seller =>
        seller.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        seller.salesTrackerId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        // Handle undefined values
        if (aValue === undefined && bValue === undefined) return 0;
        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sortableData;
  }, [coachingData, sortConfig, searchTerm]);

  const teamAverages = useMemo((): TeamAverages | Record<string, never> => {
    if (coachingData.length === 0) return {};
    
    const totals = coachingData.reduce((acc, seller) => ({
      totalSales: acc.totalSales + seller.totalSales,
      totalCalls: acc.totalCalls + seller.totalCalls,
      loginHours: acc.loginHours + parseFloat(String(seller.loginHours || 0)),
      utilization: acc.utilization + parseFloat(String(seller.averageUtilization || 0)),
      talkPercent: acc.talkPercent + parseFloat(String(seller.averageTalkPercent || 0)),
      hoursCommitted: acc.hoursCommitted + parseFloat(String(seller.hoursCommitted || 0))
    }), {
      totalSales: 0,
      totalCalls: 0,
      loginHours: 0,
      utilization: 0,
      talkPercent: 0,
      hoursCommitted: 0
    });
    
    return {
      totalSales: totals.totalSales,
      avgSales: (totals.totalSales / coachingData.length).toFixed(1),
      totalCalls: totals.totalCalls,
      avgCalls: (totals.totalCalls / coachingData.length).toFixed(0),
      avgLoginHours: (totals.loginHours / coachingData.length).toFixed(1),
      avgUtilization: (totals.utilization / coachingData.length).toFixed(1),
      avgTalkPercent: (totals.talkPercent / coachingData.length).toFixed(1),
      avgHoursCommitted: (totals.hoursCommitted / coachingData.length).toFixed(1)
    };
  }, [coachingData]);

  const getUtilizationColor = (utilization: string | number): UtilizationColor => {
    const util = parseFloat(String(utilization));
    if (util >= 70) return 'success';
    if (util >= 50) return 'warning';
    return 'error';
  };

  const getCellColor = (value: string | number, threshold: number, isGreaterThan: boolean = true): string => {
    const num = parseFloat(String(value));
    if (isGreaterThan) {
      return num >= threshold ? '#c8e6c9' : '#ffcdd2'; // light green : light red
    }
    return num < threshold ? '#c8e6c9' : '#ffcdd2';
  };

  const getHoursColor = (loginHours: string | number, hoursCommitted: string | number): string => {
    const login = parseFloat(String(loginHours));
    const committed = parseFloat(String(hoursCommitted));
    return login >= committed ? '#c8e6c9' : '#ffcdd2'; // light green : light red
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box display="flex" justifyContent="center">
          <IconButton onClick={fetchCoachingData} color="primary" size="large">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Typography variant="h4" fontWeight="600" color="primary">
            Weekly Coaching Dashboard
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={dateFilter}
                onChange={(e: SelectChangeEvent<DateFilter>) => setDateFilter(e.target.value as DateFilter)}
                displayEmpty
              >
                <MenuItem value="currentWeek">Current Week</MenuItem>
                <MenuItem value="previousWeek">Previous Week</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchCoachingData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Week of {weekRange.start} - {weekRange.end}
        </Typography>
      </Box>

      {/* Team Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUpIcon sx={{ color: '#2196F3', mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Total Sales
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="600">
                {'totalSales' in teamAverages ? teamAverages.totalSales : 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg: {'avgSales' in teamAverages ? teamAverages.avgSales : 0} per seller
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <PhoneIcon sx={{ color: '#4caf50', mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Total Calls
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="600">
                {'totalCalls' in teamAverages ? teamAverages.totalCalls : 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg: {'avgCalls' in teamAverages ? teamAverages.avgCalls : 0} per seller
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <ScheduleIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Avg Login Hours
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="600">
                {'avgLoginHours' in teamAverages ? teamAverages.avgLoginHours : 0}h
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <ShowChartIcon sx={{ color: '#9c27b0', mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Avg Utilization
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="600">
                {'avgUtilization' in teamAverages ? teamAverages.avgUtilization : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <ShowChartIcon sx={{ color: '#00bcd4', mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Avg Talk %
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="600">
                {'avgTalkPercent' in teamAverages ? teamAverages.avgTalkPercent : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <EventNoteIcon sx={{ color: '#3f51b5', mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Avg Hours Committed
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="600">
                {'avgHoursCommitted' in teamAverages ? teamAverages.avgHoursCommitted : 0}h
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by seller name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {/* Seller Performance Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'username'}
                  direction={sortConfig.key === 'username' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('username')}
                >
                  <Typography variant="subtitle2" fontWeight="600">
                    Seller
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortConfig.key === 'totalSales'}
                  direction={sortConfig.key === 'totalSales' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('totalSales')}
                >
                  <Typography variant="subtitle2" fontWeight="600">
                    Sales
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortConfig.key === 'totalCalls'}
                  direction={sortConfig.key === 'totalCalls' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('totalCalls')}
                >
                  <Typography variant="subtitle2" fontWeight="600">
                    Calls
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortConfig.key === 'loginHours'}
                  direction={sortConfig.key === 'loginHours' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('loginHours')}
                >
                  <Typography variant="subtitle2" fontWeight="600">
                    Login Hours
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortConfig.key === 'averageUtilization'}
                  direction={sortConfig.key === 'averageUtilization' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('averageUtilization')}
                >
                  <Typography variant="subtitle2" fontWeight="600">
                    Utilization %
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortConfig.key === 'averageTalkPercent'}
                  direction={sortConfig.key === 'averageTalkPercent' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('averageTalkPercent')}
                >
                  <Typography variant="subtitle2" fontWeight="600">
                    Talk %
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortConfig.key === 'hoursCommitted'}
                  direction={sortConfig.key === 'hoursCommitted' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('hoursCommitted')}
                >
                  <Typography variant="subtitle2" fontWeight="600">
                    Hours Committed
                  </Typography>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchTerm ? 'No sellers found matching your search' : 'No data available for previous week'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((seller) => (
                <TableRow key={seller.userId} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {seller.username}
                      </Typography>
                      {seller.salesTrackerId && (
                        <Typography variant="caption" color="text.secondary">
                          {seller.salesTrackerId}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: getCellColor(seller.totalSales, 10) }}>
                    <Typography variant="h6" fontWeight="600" color="primary">
                      {seller.totalSales}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: getCellColor(seller.totalCalls, 1000) }}>
                    <Typography variant="body2">
                      {seller.totalCalls}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: getHoursColor(seller.loginHours, seller.hoursCommitted) }}>
                    <Typography variant="body2">
                      {parseFloat(String(seller.loginHours)).toFixed(1)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`${parseFloat(String(seller.averageUtilization)).toFixed(1)}%`}
                      color={getUtilizationColor(seller.averageUtilization)}
                      size="small"
                      sx={{ fontWeight: 600, minWidth: 60 }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: getCellColor(seller.averageTalkPercent, 30) }}>
                    <Typography variant="body2">
                      {parseFloat(String(seller.averageTalkPercent)).toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: getHoursColor(seller.loginHours, seller.hoursCommitted) }}>
                    <Typography variant="body2">
                      {parseFloat(String(seller.hoursCommitted)).toFixed(1)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Paper elevation={1} sx={{ mt: 3, p: 2, bgcolor: '#f9f9f9' }}>
        <Typography variant="subtitle2" fontWeight="600" gutterBottom>
          Utilization Color Coding:
        </Typography>
        <Box display="flex" gap={3} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label="70%+" color="success" size="small" />
            <Typography variant="caption" color="text.secondary">
              Excellent Performance
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label="50-69%" color="warning" size="small" />
            <Typography variant="caption" color="text.secondary">
              Needs Improvement
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label="<50%" color="error" size="small" />
            <Typography variant="caption" color="text.secondary">
              Action Required
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PreviousWeekCoaching;
